// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    CategoryMarginModel,
    ProductMarginModel,
    RetailerModel,
    RetailerPriceManagementState,
} from "@synd/App/Store/Pages/RetailerPriceManagement/RetailerPriceManagementState";
import { Draft } from "immer";

export const initialState: RetailerPriceManagementState = {
    retailer: null,
    retailerCategories: [],
    isLoadingCategories: false,
    productMargin: null,
    isLoadingProductMargin: false,
};

const reducer = {
    "RetailerPriceManagement/SetRetailer": (
        draft: Draft<RetailerPriceManagementState>,
        action: { retailer: RetailerModel | null },
    ) => {
        draft.retailer = action.retailer;
        // Reset categories when retailer changes
        if (!action.retailer) {
            draft.retailerCategories = [];
        }
    },

    "RetailerPriceManagement/BeginLoadCategories": (draft: Draft<RetailerPriceManagementState>) => {
        draft.isLoadingCategories = true;
    },

    "RetailerPriceManagement/CompleteLoadCategories": (
        draft: Draft<RetailerPriceManagementState>,
        action: { categories: CategoryMarginModel[] },
    ) => {
        draft.retailerCategories = action.categories;
        draft.isLoadingCategories = false;
    },

    "RetailerPriceManagement/UpdateCategoryMargin": (
        draft: Draft<RetailerPriceManagementState>,
        action: { categoryId: string; newMargin: number },
    ) => {
        const category = draft.retailerCategories.find(c => c.categoryId === action.categoryId);
        if (category) {
            category.margin = action.newMargin;
        }
    },

    "RetailerPriceManagement/SetIsLoadingProductMargin": (
        draft: Draft<RetailerPriceManagementState>,
        action: { isLoading: boolean },
    ) => {
        draft.isLoadingProductMargin = action.isLoading;
    },

    "RetailerPriceManagement/SetProductMargin": (
        draft: Draft<RetailerPriceManagementState>,
        action: { productMargin: ProductMarginModel },
    ) => {
        draft.productMargin = action.productMargin;
    },

    "RetailerPriceManagement/ClearProductMargin": (draft: Draft<RetailerPriceManagementState>) => {
        draft.productMargin = null;
    },
};

const xRetailerPriceManagement = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xRetailerPriceManagement = xRetailerPriceManagement;

export type xRetailerPriceManagementState = {
    xRetailerPriceManagement: RetailerPriceManagementState;
} & ApplicationState;

type xRetailerPriceManagementReducers = {
    [key: string]: typeof xRetailerPriceManagement;
};

export type xRetailerPriceManagementActions =
    | Parameters<xRetailerPriceManagementReducers[keyof xRetailerPriceManagementReducers]>[1]
    | AnyAction;


